<template>
    <div class="rr-detail-overlay" v-if="show" @click.self="close">
        <div class="resume-request-modal">
            <div class="resume-request-modal-header">
                <button
                    type="button"
                    class="rr-modal-nav-btn"
                    :disabled="!hasPrevious && !canLoadPreviousPage"
                    @click.prevent="previous"
                >
                    &lt; {{ previousButtonText }}
                </button>
                <span class="rr-detail-close" @click="close">&times;</span>
                <button
                    type="button"
                    class="rr-modal-nav-btn"
                    :disabled="!hasNext && !canLoadNextPage"
                    @click.prevent="next"
                >
                    {{ nextButtonText }} &gt;
                </button>
            </div>

            <div class="resume-request-modal-body" v-if="resumeRequest && !isLoading">
                <h2 class="resume-request-detail-title">Resume Request Details</h2>

                <div
                    v-if="message"
                    class="resume-request-message"
                    :class="messageType === 'error' ? 'resume-request-message--error' : 'resume-request-message--success'"
                    style="display: block;"
                >
                    {{ message }}
                </div>

                <div class="rr-detail-section">
                    <h3>Patient Information</h3>
                    <div class="rr-detail-grid">
                        <div class="rr-detail-item">
                            <label>Name:</label>
                            <span>
                                <a :href="'/dashboard/patients/' + resumeRequest.patient_id">{{ patientName(resumeRequest) }}</a>
                            </span>
                        </div>
                        <div class="rr-detail-item" v-if="person.email">
                            <label>Email:</label>
                            <span>{{ person.email }}</span>
                        </div>
                        <div class="rr-detail-item" v-if="person.phone">
                            <label>Phone:</label>
                            <span>{{ person.phone }}</span>
                        </div>
                        <div class="rr-detail-item" v-if="person.date_of_birth">
                            <label>Date Of Birth:</label>
                            <span>{{ localTime(person.date_of_birth, 'M/D/YYYY') }}</span>
                        </div>
                    </div>
                </div>

                <div class="rr-detail-section">
                    <h3>Request Information</h3>
                    <div class="rr-detail-grid">
                        <div class="rr-detail-item">
                            <label>Product:</label>
                            <span>{{ resumeRequest.product_name || 'N/A' }}</span>
                        </div>
                        <div class="rr-detail-item">
                            <label>Medication/Strength:</label>
                            <span>{{ resumeRequest.strength || 'N/A' }}</span>
                        </div>
                        <div class="rr-detail-item">
                            <label>Was Using GLP-1:</label>
                            <span>{{ resumeRequest.was_taking ? 'Yes' : 'No' }}</span>
                        </div>
                        <div class="rr-detail-item">
                            <label>Status:</label>
                            <span class="rr-status-badge" :class="statusClass(resumeRequest.status)">
                                {{ resumeRequest.status || 'Pending' }}
                            </span>
                        </div>
                        <div class="rr-detail-item" v-if="resumeRequest.consultation_id">
                            <label>Consultation:</label>
                            <span>
                                <a :href="'/dashboard/consultations/' + resumeRequest.consultation_id">
                                    {{ resumeRequest.consultation_id }}
                                </a>
                            </span>
                        </div>
                        <div class="rr-detail-item">
                            <label>Submitted By:</label>
                            <span>{{ fullName(resumeRequest.submitted_by) }}</span>
                        </div>
                        <div class="rr-detail-item" v-if="resumeRequest.reviewed_by">
                            <label>Reviewed By:</label>
                            <span>{{ fullName(resumeRequest.reviewed_by) }}</span>
                        </div>
                        <div class="rr-detail-item">
                            <label>Created At:</label>
                            <span>{{ localTime(resumeRequest.created_at) }}</span>
                        </div>
                    </div>
                </div>

                <div class="rr-modal-actions">
                    <button
                        type="button"
                        class="rr-btn-accept"
                        :disabled="!isPending || isProcessing"
                        @click="review('resume')"
                    >
                        Accept
                    </button>
                    <button
                        type="button"
                        class="rr-btn-reject"
                        :disabled="!isPending || isProcessing"
                        @click="review('reject')"
                    >
                        Reject
                    </button>
                </div>
            </div>

            <div v-else-if="isLoading" class="rr-modal-loading">
                <p>Loading...</p>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { apiMessage, localTime, patientName, statusClass } from './resumeRequestHelpers';

export default {
    name: 'ResumeRequestDetailModal',

    props: {
        show: { type: Boolean, default: false },
        resumeRequest: { type: Object, default: null },
        allRequests: { type: Array, default: function () { return []; } },
        currentPage: { type: Number, default: 0 },
        totalPages: { type: Number, default: 0 },
    },

    data() {
        return {
            isProcessing: false,
            isLoading: false,
            message: '',
            messageType: '',
        };
    },

    computed: {
        person() {
            return (this.resumeRequest && this.resumeRequest.person) || {};
        },
        isPending() {
            return this.resumeRequest && this.resumeRequest.status === 'Pending';
        },
        currentIndex() {
            if (!this.resumeRequest) {
                return -1;
            }

            return this.allRequests.findIndex((request) => request.id === this.resumeRequest.id);
        },
        hasPrevious() {
            return this.currentIndex > 0;
        },
        hasNext() {
            return this.currentIndex >= 0 && this.currentIndex < this.allRequests.length - 1;
        },
        canLoadPreviousPage() {
            return this.currentIndex === 0 && this.currentPage > 0;
        },
        canLoadNextPage() {
            return this.currentIndex === this.allRequests.length - 1
                && this.currentPage < this.totalPages - 1;
        },
        previousButtonText() {
            return this.canLoadPreviousPage ? 'Previous Page' : 'Previous';
        },
        nextButtonText() {
            return this.canLoadNextPage ? 'Next Page' : 'Next';
        },
    },

    watch: {
        resumeRequest() {
            this.isLoading = false;
            this.message = '';
        },
        show(value) {
            if (value) {
                this.message = '';
                this.messageType = '';
            }
        },
    },

    methods: {
        patientName,
        statusClass,
        localTime,

        fullName(person) {
            if (!person) {
                return '—';
            }

            return `${person.first_name || ''} ${person.last_name || ''}`.trim() || person.email || '—';
        },

        close() {
            this.message = '';
            this.messageType = '';
            this.$emit('close');
        },

        previous() {
            if (this.hasPrevious) {
                this.$emit('previous');
            } else if (this.canLoadPreviousPage) {
                this.isLoading = true;
                this.$emit('load-page', this.currentPage - 1);
            }
        },

        next() {
            if (this.hasNext) {
                this.$emit('next');
            } else if (this.canLoadNextPage) {
                this.isLoading = true;
                this.$emit('load-page', this.currentPage + 1);
            }
        },

        review(action) {
            if (!this.isPending || this.isProcessing) {
                return;
            }

            const fallback = action === 'resume'
                ? 'Failed to accept resume request. Please try again.'
                : 'Failed to reject resume request. Please try again.';

            this.isProcessing = true;
            this.message = '';
            this.messageType = '';

            axios
                .post(`/api/v1/resume-requests/${this.resumeRequest.id}/${action}`)
                .then(() => {
                    this.$emit('updated');
                    this.close();
                })
                .catch((error) => {
                    this.message = apiMessage(error, fallback);
                    this.messageType = 'error';
                })
                .finally(() => {
                    this.isProcessing = false;
                });
        },
    },
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/ResumeRequestDetailModal.vue